// Central adapter to build coach datasets from unified dashboard data
import { CoachData, DashboardActivity } from '@/types/dashboard';

function formatPace(seconds?: number) {
  if (!seconds && seconds !== 0) return 'N/A';
  const m = Math.floor((seconds || 0) / 60);
  const s = Math.round((seconds || 0) % 60).toString().padStart(2, '0');
  return `${m}:${s}/km`;
}

export const DashboardDataAdapter = {
  runsToActivities(runs: any[] = []): DashboardActivity[] {
    return runs.slice(0, 3).map((run) => ({
      id: run.id,
      date: run.recorded_date,
      type: 'running' as const,
      summary: `${((run.distance_meters || 0) / 1000).toFixed(1)}km run`,
      metrics: {
        distance: run.distance_meters,
        time: run.time_seconds,
        pace: run.pace_per_km_seconds,
        heartRate: run.heart_rate_avg,
      },
      details: [
        run.distance_meters ? `${(run.distance_meters / 1000).toFixed(1)}km` : 'N/A',
        formatPace(run.pace_per_km_seconds),
        run.heart_rate_avg ? `${run.heart_rate_avg}bpm` : null,
      ].filter(Boolean) as string[],
    }));
  },

  fitnessToActivities(activities: any[] = []): DashboardActivity[] {
    return activities.slice(0, 3).map((a) => ({
      id: a.id,
      date: a.recorded_date,
      type: 'fitness' as const,
      summary: `${a.activity_type} workout`,
      metrics: {
        duration: a.duration_seconds,
        calories: a.calories_burned,
        intensity: a.intensity_level,
      },
      details: [
        a.duration_seconds ? `${Math.round(a.duration_seconds / 60)}min` : 'N/A',
        a.calories_burned ? `${a.calories_burned}cal` : null,
      ].filter(Boolean) as string[],
    }));
  },

  buildRunningCoachData(recentRuns: any[] = []): CoachData | null {
    if (!recentRuns) return null;

    const stravaData = recentRuns.filter((r) => r.data_source === 'strava');
    const hasStravaData = stravaData.length > 0;

    const getNextSuggestion = () => {
      if (recentRuns.length === 0)
        return 'Start with 20-minute easy runs, 3 times per week';

      const daysSinceLastRun = Math.floor(
        (Date.now() - new Date(recentRuns[0].recorded_date).getTime()) /
          (1000 * 60 * 60 * 24)
      );
      const recentFiveRuns = recentRuns.slice(0, 5);
      const olderFiveRuns = recentRuns.slice(5, 10);

      const recentAvgPace =
        recentFiveRuns.length > 0
          ? recentFiveRuns.reduce((sum, r) => sum + (r.pace_per_km_seconds || 0), 0) /
            recentFiveRuns.length
          : 0;
      const olderAvgPace =
        olderFiveRuns.length > 0
          ? olderFiveRuns.reduce((sum, r) => sum + (r.pace_per_km_seconds || 0), 0) /
            olderFiveRuns.length
          : 0;
      const paceImprovement =
        olderAvgPace > 0 && recentAvgPace > 0
          ? ((olderAvgPace - recentAvgPace) / olderAvgPace) * 100
          : 0;

      if (daysSinceLastRun > 3) return 'Schedule an easy recovery run to maintain momentum';
      if (paceImprovement > 5) return 'Try a tempo run to build on your pace gains';
      if (hasStravaData && stravaData.some((r) => r.suffer_score && r.suffer_score > 60))
        return 'Consider an easy run to aid recovery';
      return 'Mix in some interval training to boost performance';
    };

    return {
      recentRuns: recentRuns.slice(0, 5),
      metrics: [],
      insights: [],
      nextSuggestion: getNextSuggestion(),
      recentActivities: this.runsToActivities(recentRuns),
      hasExternalData: hasStravaData,
    };
  },

  buildFitnessCoachData(recentFitness: any[] = []): CoachData | null {
    if (!recentFitness) return null;

    const activityTypes = [...new Set(recentFitness.map((a) => a.activity_type))];

    const getNextSuggestion = () => {
      if (recentFitness.length === 0) return 'Start with activities you enjoy most';

      const weeklyActivities = recentFitness.slice(0, 7);
      const strengthActivities = weeklyActivities.filter((a) =>
        ['strength', 'weight_training', 'crossfit', 'gym'].includes(
          a.activity_type?.toLowerCase()
        )
      ).length;
      const cardioActivities = weeklyActivities.filter((a) =>
        ['cycling', 'swimming', 'rowing', 'elliptical', 'cardio'].includes(
          a.activity_type?.toLowerCase()
        )
      ).length;
      const daysSinceLastActivity = Math.floor(
        (Date.now() - new Date(recentFitness[0].recorded_date).getTime()) /
          (1000 * 60 * 60 * 24)
      );

      if (daysSinceLastActivity > 2) return 'Schedule a workout to maintain consistency';
      if (strengthActivities === 0 && weeklyActivities.length > 0)
        return 'Add strength training for balanced fitness';
      if (cardioActivities === 0 && weeklyActivities.length > 0)
        return 'Include cardio for heart health';
      if (activityTypes.length < 3) return 'Try a new activity type for variety';
      return 'Keep up your diverse training approach';
    };

    return {
      recentRuns: [],
      metrics: [],
      insights: [],
      nextSuggestion: getNextSuggestion(),
      recentActivities: this.fitnessToActivities(recentFitness),
      hasExternalData: false,
    };
  },
};
